import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { useAuth } from '../hooks/use-auth';
import { Customer } from '../entities/customer';
import * as customersService from '../services/api/customers';
import { parameters } from '../constants/parameters';
import { MessageId } from '../entities/message-id';

interface ProviderProps {
  children: React.ReactNode;
}

interface ContextValue {
  customers: Customer[];
  isLoading: boolean;
  lastMessageReceived: MessageId | null;
  setRead: (customerId: number, text: string) => void;
  resolve: (customerId: number, resolved: boolean) => void;
  updateLastMessage: (customerId: number, messageId: string, text: string, isUnread: boolean) => void;
}

const CustomersContext = createContext<ContextValue>({
  customers: [],
  isLoading: true,
  lastMessageReceived: null,
  updateLastMessage: () => null,
  setRead: () => null,
  resolve: () => null,
});

function CustomersContextProvider({ children }: ProviderProps) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [lastMessage, setLastMessage] = useState<MessageId | null>(null);

  const { token } = useAuth();

  const setRead = useCallback((customerId: number, text: string): void => {
    setCustomers((prevCustomers) =>
      prevCustomers.map((c) => {
        if (c.id !== customerId) return c;

        return {
          ...c,
          hasUnreadMessage: false,
          lastMessage: c.lastMessage ? { ...c.lastMessage, message: text } : c.lastMessage,
        };
      }),
    );
  }, []);

  const resolve = useCallback((customerId: number, resolved: boolean): void => {
    setCustomers((prevCustomers) =>
      prevCustomers.map((c) => {
        if (c.id !== customerId) return c;

        return { ...c, resolved };
      }),
    );
  }, []);

  const updateLastMessage = useCallback(
    (customerId: number, messageId: string, text: string, isUnread: boolean): void => {
      setCustomers((prevCustomers) => {
        const customerIndex = prevCustomers.findIndex((c) => c.id === customerId);

        if (customerIndex === -1) return prevCustomers;

        const updatedCustomers = prevCustomers.map((c, index) => {
          if (index !== customerIndex) return c;

          return {
            ...c,
            lastMessageSentAt: new Date().toISOString(),
            hasUnreadMessage: isUnread,
            lastMessage: {
              isCreatedByCustomer: false,
              messageId,
              message: text,
              customerId: c.id,
              administratorId: -1,
              createdAt: new Date().getTime(),
            },
          };
        });

        return [...updatedCustomers].sort((a, b) => {
          if (!a.lastMessageSentAt && !b.lastMessageSentAt) return 0;
          if (!a.lastMessageSentAt) return 1;
          if (!b.lastMessageSentAt) return -1;

          return a.lastMessageSentAt > b.lastMessageSentAt ? -1 : 1;
        });
      });
    },
    [],
  );

  useEffect(() => {
    const loadData = async () => {
      const items = await customersService.list(token);

      setCustomers(items);
      setIsLoading(false);
    };

    loadData();
  }, [token]);

  useEffect(() => {
    let ws: WebSocket | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      ws = new WebSocket(`${parameters.api.websocketUrl}?token=${token}`);

      ws.onmessage = (event) => {
        const messageDetails = JSON.parse(event.data);

        updateLastMessage(messageDetails.customerId, messageDetails.messageId, 'New message received', true);
        setLastMessage(messageDetails);
      };

      ws.onerror = () => {
        ws = null;
        reconnectTimeout = setTimeout(connect, 5000);
      };

      ws.onclose = () => {
        ws = null;
        reconnectTimeout = setTimeout(connect, 5000);
      };
    };

    connect();

    return () => {
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (ws) ws.close();
    };
  }, [token, updateLastMessage]);

  const contextValue = useMemo(
    () => ({
      customers,
      isLoading,
      lastMessageReceived: lastMessage,
      updateLastMessage,
      setRead,
      resolve,
    }),
    [customers, lastMessage, setRead, isLoading, updateLastMessage, resolve],
  );

  return <CustomersContext.Provider value={contextValue}>{children}</CustomersContext.Provider>;
}

CustomersContextProvider.defaultProps = {};

export { CustomersContext, CustomersContextProvider };
